import React, { useState, useEffect, useCallback } from 'react';
import { permissionsApi, PermissionCategory, UserPermissionsResponse } from '../../api/permissionsApi';
import { departmentTimesheetAccessApi, DepartmentTimesheetAccess, DepartmentForAccess, DepartmentAccessAssignment } from '../../api/departmentTimesheetAccessApi';
import { usersApi } from '../../api/usersApi';
import { rolesApi } from '../../api/rolesApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import { User, Role } from '../../types';
import { stringToColor, getInitials } from '../../utils/helpers';
import './Permissions.css';

type TabType = 'users' | 'roles' | 'department-access';

export function Permissions() {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Permissions data
  const [categories, setCategories] = useState<PermissionCategory[]>([]);
  
  // Users tab state - with search & pagination
  const [users, setUsers] = useState<User[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userTotalCount, setUserTotalCount] = useState(0);
  const [userListLoading, setUserListLoading] = useState(false);
  const userPerPage = 20;
  
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissionsResponse | null>(null);
  const [userOverrides, setUserOverrides] = useState<Record<string, boolean>>({});
  const [savingUser, setSavingUser] = useState(false);
  
  // Roles tab state
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [rolePermissions, setRolePermissions] = useState<Record<string, boolean>>({});
  const [savingRole, setSavingRole] = useState(false);

  // Department access tab state
  const [selectedUserForDept, setSelectedUserForDept] = useState<User | null>(null);
  const [userDeptAccess, setUserDeptAccess] = useState<DepartmentTimesheetAccess[]>([]);
  const [allDepartments, setAllDepartments] = useState<DepartmentForAccess[]>([]);
  const [deptAccessEdits, setDeptAccessEdits] = useState<Record<number, DepartmentAccessAssignment>>({});
  const [savingDeptAccess, setSavingDeptAccess] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  
  // Department access tab - user list with search & pagination
  const [deptTabUsers, setDeptTabUsers] = useState<User[]>([]);
  const [deptTabSearchTerm, setDeptTabSearchTerm] = useState('');
  const [deptTabCurrentPage, setDeptTabCurrentPage] = useState(1);
  const [deptTabTotalPages, setDeptTabTotalPages] = useState(1);
  const [deptTabTotalUsers, setDeptTabTotalUsers] = useState(0);
  const [deptTabLoading, setDeptTabLoading] = useState(false);
  const deptTabPerPage = 20;

  // Fetch all permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await permissionsApi.getAllPermissions();
      setCategories(response.categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    }
  }, []);

  // Fetch users for User Permissions tab with search & pagination
  const fetchUsers = useCallback(async (page: number, search: string) => {
    try {
      setUserListLoading(true);
      const response = await usersApi.getUsers({ 
        page, 
        per_page: userPerPage, 
        search: search || undefined 
      });
      setUsers(response.users);
      setUserTotalPages(response.pages || 1);
      setUserTotalCount(response.total || 0);
      setUserCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUserListLoading(false);
    }
  }, [userPerPage]);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await rolesApi.getRoles();
      setRoles(response.roles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    }
  }, []);

  // Fetch departments for access
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await departmentTimesheetAccessApi.getDepartments();
      setAllDepartments(response.departments);
    } catch (err) {
      // Non-critical, don't show error
      console.error('Failed to load departments:', err);
    }
  }, []);

  // Fetch users for department access tab with search & pagination
  const fetchDeptTabUsers = useCallback(async (page: number, search: string) => {
    try {
      setDeptTabLoading(true);
      const response = await usersApi.getUsers({ 
        page, 
        per_page: deptTabPerPage, 
        search: search || undefined 
      });
      setDeptTabUsers(response.users);
      setDeptTabTotalPages(response.pages || 1);
      setDeptTabTotalUsers(response.total || 0);
      setDeptTabCurrentPage(page);
    } catch (err) {
      console.error('Failed to load users for department access tab:', err);
    } finally {
      setDeptTabLoading(false);
    }
  }, [deptTabPerPage]);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPermissions(), fetchUsers(1, ''), fetchRoles(), fetchDepartments(), fetchDeptTabUsers(1, '')]);
      setLoading(false);
    };
    loadData();
  }, [fetchPermissions, fetchUsers, fetchRoles, fetchDepartments, fetchDeptTabUsers]);

  // Load user permissions when user is selected
  const handleSelectUser = async (user: User) => {
    setSelectedUser(user);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await permissionsApi.getUserPermissions(user.id);
      setUserPermissions(response);
      setUserOverrides(response.overrides || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user permissions');
    }
  };

  // Handle user permission toggle
  const handleUserPermissionToggle = (permKey: string) => {
    if (!userPermissions) return;
    
    const roleGranted = userPermissions.role_permissions[permKey] === true;
    const currentOverride = userOverrides[permKey];
    
    // Toggle logic:
    // - If role grants and no override: add override to revoke (false)
    // - If role grants and override=false: remove override (use role default)
    // - If role doesn't grant and no override: add override to grant (true)
    // - If role doesn't grant and override=true: remove override (use role default)
    
    const newOverrides = { ...userOverrides };
    
    if (currentOverride === undefined) {
      // No override yet - add one
      newOverrides[permKey] = !roleGranted;
    } else {
      // Has override - remove it
      delete newOverrides[permKey];
    }
    
    setUserOverrides(newOverrides);
  };

  // Save user permissions
  const handleSaveUserPermissions = async () => {
    if (!selectedUser) return;
    
    setSavingUser(true);
    setError(null);
    setSuccess(null);
    
    try {
      await permissionsApi.updateUserPermissions(selectedUser.id, {
        overrides: userOverrides,
      });
      setSuccess(`Permissions saved for ${selectedUser.name}`);
      
      // Refresh user permissions
      const response = await permissionsApi.getUserPermissions(selectedUser.id);
      setUserPermissions(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save permissions');
    } finally {
      setSavingUser(false);
    }
  };

  // Reset user to role defaults
  const handleResetUserPermissions = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm(`Reset ${selectedUser.name} to role defaults? All permission overrides will be removed.`)) {
      return;
    }
    
    setSavingUser(true);
    setError(null);
    
    try {
      await permissionsApi.resetUserPermissions(selectedUser.id);
      setUserOverrides({});
      setSuccess(`${selectedUser.name} reset to role defaults`);
      
      // Refresh user permissions
      const response = await permissionsApi.getUserPermissions(selectedUser.id);
      setUserPermissions(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset permissions');
    } finally {
      setSavingUser(false);
    }
  };

  // Handle role selection
  const handleSelectRole = (role: Role) => {
    setSelectedRole(role);
    setRolePermissions(role.permissions || {});
    setError(null);
    setSuccess(null);
  };

  // Handle user selection for department access
  const handleSelectUserForDept = async (user: User) => {
    setSelectedUserForDept(user);
    setError(null);
    setSuccess(null);
    setShowAddDepartment(false);
    
    try {
      const response = await departmentTimesheetAccessApi.getUserAccess(user.id);
      setUserDeptAccess(response.access_records);
      
      // Initialize edits from existing access
      const edits: Record<number, DepartmentAccessAssignment> = {};
      response.access_records.forEach(record => {
        edits[record.department_id] = {
          department_id: record.department_id,
          can_view: record.can_view,
          can_approve: record.can_approve,
          can_reject: record.can_reject,
          can_update: record.can_update,
        };
      });
      setDeptAccessEdits(edits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user department access');
    }
  };

  // Handle department permission toggle
  const handleDeptPermissionToggle = (departmentId: number, permission: 'can_view' | 'can_approve' | 'can_reject' | 'can_update') => {
    setDeptAccessEdits(prev => {
      const existing = prev[departmentId] || {
        department_id: departmentId,
        can_view: false,
        can_approve: false,
        can_reject: false,
        can_update: false,
      };
      return {
        ...prev,
        [departmentId]: {
          ...existing,
          [permission]: !existing[permission],
        },
      };
    });
  };

  // Add a new department to user's access
  const handleAddDepartment = (departmentId: number) => {
    if (deptAccessEdits[departmentId]) return; // Already added
    
    setDeptAccessEdits(prev => ({
      ...prev,
      [departmentId]: {
        department_id: departmentId,
        can_view: false,
        can_approve: false,
        can_reject: false,
        can_update: false,
      },
    }));
    setShowAddDepartment(false);
  };

  // Remove department access
  const handleRemoveDepartment = (departmentId: number) => {
    setDeptAccessEdits(prev => {
      const newEdits = { ...prev };
      // Set all permissions to false (will be removed on save)
      newEdits[departmentId] = {
        department_id: departmentId,
        can_view: false,
        can_approve: false,
        can_reject: false,
        can_update: false,
      };
      return newEdits;
    });
  };

  // Save department access
  const handleSaveDeptAccess = async () => {
    if (!selectedUserForDept) return;
    
    setSavingDeptAccess(true);
    setError(null);
    setSuccess(null);
    
    try {
      const assignments = Object.values(deptAccessEdits);
      await departmentTimesheetAccessApi.updateUserAccess(selectedUserForDept.id, { assignments });
      setSuccess(`Department access saved for ${selectedUserForDept.name}`);
      
      // Refresh user access
      const response = await departmentTimesheetAccessApi.getUserAccess(selectedUserForDept.id);
      setUserDeptAccess(response.access_records);
      
      // Update edits to reflect saved state
      const edits: Record<number, DepartmentAccessAssignment> = {};
      response.access_records.forEach(record => {
        edits[record.department_id] = {
          department_id: record.department_id,
          can_view: record.can_view,
          can_approve: record.can_approve,
          can_reject: record.can_reject,
          can_update: record.can_update,
        };
      });
      setDeptAccessEdits(edits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save department access');
    } finally {
      setSavingDeptAccess(false);
    }
  };

  // Reset department access
  const handleResetDeptAccess = async () => {
    if (!selectedUserForDept) return;
    
    if (!window.confirm(`Remove all department timesheet access for ${selectedUserForDept.name}?`)) {
      return;
    }
    
    setSavingDeptAccess(true);
    setError(null);
    
    try {
      await departmentTimesheetAccessApi.resetUserAccess(selectedUserForDept.id);
      setUserDeptAccess([]);
      setDeptAccessEdits({});
      setSuccess(`Department access cleared for ${selectedUserForDept.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset department access');
    } finally {
      setSavingDeptAccess(false);
    }
  };

  // Get departments available to add (not already assigned)
  const getAvailableDepartments = () => {
    const assignedDeptIds = new Set(Object.keys(deptAccessEdits).map(Number));
    return allDepartments.filter(d => !assignedDeptIds.has(d.id));
  };

  // Check if any department has unsaved changes
  const hasDeptAccessChanges = () => {
    const currentIds = new Set(userDeptAccess.map(r => r.department_id));
    const editIdList = Object.keys(deptAccessEdits).map(Number);
    
    // Check for new departments
    for (let i = 0; i < editIdList.length; i++) {
      const id = editIdList[i];
      if (!currentIds.has(id)) {
        const edit = deptAccessEdits[id];
        if (edit.can_view || edit.can_approve || edit.can_reject || edit.can_update) {
          return true;
        }
      }
    }
    
    // Check for permission changes
    for (const record of userDeptAccess) {
      const edit = deptAccessEdits[record.department_id];
      if (!edit) continue;
      if (
        edit.can_view !== record.can_view ||
        edit.can_approve !== record.can_approve ||
        edit.can_reject !== record.can_reject ||
        edit.can_update !== record.can_update
      ) {
        return true;
      }
    }
    
    return false;
  };

  // User Permissions tab - search handler
  const handleUserSearch = useCallback(() => {
    fetchUsers(1, userSearchTerm);
  }, [fetchUsers, userSearchTerm]);

  // User Permissions tab - pagination handler
  const handleUserPageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= userTotalPages) {
      fetchUsers(newPage, userSearchTerm);
    }
  }, [fetchUsers, userSearchTerm, userTotalPages]);

  // Department access tab - search handler
  const handleDeptTabSearch = useCallback(() => {
    fetchDeptTabUsers(1, deptTabSearchTerm);
  }, [fetchDeptTabUsers, deptTabSearchTerm]);

  // Department access tab - pagination handler
  const handleDeptTabPageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= deptTabTotalPages) {
      fetchDeptTabUsers(newPage, deptTabSearchTerm);
    }
  }, [fetchDeptTabUsers, deptTabSearchTerm, deptTabTotalPages]);

  // Handle role permission toggle
  const handleRolePermissionToggle = (permKey: string) => {
    setRolePermissions(prev => ({
      ...prev,
      [permKey]: !prev[permKey],
    }));
  };

  // Save role permissions
  const handleSaveRolePermissions = async () => {
    if (!selectedRole) return;
    
    setSavingRole(true);
    setError(null);
    setSuccess(null);
    
    try {
      await rolesApi.updateRole(selectedRole.id, {
        permissions: rolePermissions,
      });
      setSuccess(`Permissions saved for ${selectedRole.display_name} role`);
      
      // Refresh roles
      await fetchRoles();
      
      // Update selected role with new permissions
      setSelectedRole(prev => prev ? { ...prev, permissions: rolePermissions } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role permissions');
    } finally {
      setSavingRole(false);
    }
  };

  // Get effective permission status for display
  const getUserPermissionStatus = (permKey: string): 'granted' | 'denied' | 'override-granted' | 'override-denied' => {
    if (!userPermissions) return 'denied';
    
    const roleGranted = userPermissions.role_permissions[permKey] === true;
    const override = userOverrides[permKey];
    
    if (override !== undefined) {
      return override ? 'override-granted' : 'override-denied';
    }
    
    return roleGranted ? 'granted' : 'denied';
  };

  return (
    <div className="permissions-page">
      <header className="permissions-header">
        <div className="permissions-title">
          <h1>Permissions Management</h1>
          <p>Manage user and role permissions</p>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icons.Check />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Icons.Users />
          User Permissions
        </button>
        <button 
          className={`tab ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          <Icons.Briefcase />
          Role Defaults
        </button>
        <button 
          className={`tab ${activeTab === 'department-access' ? 'active' : ''}`}
          onClick={() => setActiveTab('department-access')}
        >
          <Icons.Building />
          Department Timesheet Access
        </button>
      </div>

      <div className="permissions-content">
        {activeTab === 'users' ? (
          <div className="two-column-layout">
            <Card className="selection-panel user-selection-panel" padding="none">
              <div className="panel-header">
                <h3>Select User</h3>
                <span className="user-count">{userTotalCount} users</span>
              </div>
              
              {/* Search Input */}
              <div className="user-search-box">
                <div className="search-input-wrapper">
                  <Icons.Search className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search by name, email, code..."
                    value={userSearchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setUserSearchTerm(val);
                      if (val === '') fetchUsers(1, '');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
                  />
                  {userSearchTerm && (
                    <button 
                      className="clear-search-btn"
                      onClick={() => {
                        setUserSearchTerm('');
                        fetchUsers(1, '');
                      }}
                      title="Clear search"
                    >
                      <Icons.X />
                    </button>
                  )}
                </div>
              </div>

              <div className="user-list">
                {userListLoading ? (
                  <div className="loading-users">Loading...</div>
                ) : users.length === 0 ? (
                  <div className="no-users-found">
                    {userSearchTerm ? 'No users match your search' : 'No users found'}
                  </div>
                ) : (
                  users.map(user => (
                    <div
                      key={user.id}
                      className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                      onClick={() => handleSelectUser(user)}
                    >
                      <div
                        className="user-avatar"
                        style={{ backgroundColor: stringToColor(user.email) }}
                      >
                        {getInitials(user.name.split(' ')[0], user.name.split(' ')[1] || '')}
                      </div>
                      <div className="user-info">
                        <span className="user-name">
                          {user.name}
                          {user.employee_code && <span className="user-code">({user.employee_code})</span>}
                        </span>
                        <span className="user-roles">
                          {user.roles?.join(', ') || 'No roles'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {userTotalPages > 1 && (
                <div className="user-list-pagination">
                  <button 
                    className="pagination-btn" 
                    onClick={() => handleUserPageChange(1)} 
                    disabled={userCurrentPage === 1 || userListLoading}
                    title="First page"
                  >
                    <Icons.ChevronsLeft />
                  </button>
                  <button 
                    className="pagination-btn" 
                    onClick={() => handleUserPageChange(userCurrentPage - 1)} 
                    disabled={userCurrentPage === 1 || userListLoading}
                    title="Previous page"
                  >
                    <Icons.ChevronLeft />
                  </button>
                  
                  <span className="pagination-info">
                    {userCurrentPage} / {userTotalPages}
                  </span>

                  <button 
                    className="pagination-btn" 
                    onClick={() => handleUserPageChange(userCurrentPage + 1)} 
                    disabled={userCurrentPage === userTotalPages || userListLoading}
                    title="Next page"
                  >
                    <Icons.ChevronRight />
                  </button>
                  <button 
                    className="pagination-btn" 
                    onClick={() => handleUserPageChange(userTotalPages)} 
                    disabled={userCurrentPage === userTotalPages || userListLoading}
                    title="Last page"
                  >
                    <Icons.ChevronsRight />
                  </button>
                </div>
              )}
            </Card>

            <Card className="permissions-panel">
              {selectedUser && userPermissions ? (
                <>
                  <div className="panel-header">
                    <div>
                      <h3>{selectedUser.name}</h3>
                      <p className="panel-subtitle">
                        Roles: {userPermissions.roles.join(', ')} • 
                        {Object.keys(userOverrides).length} override(s)
                      </p>
                    </div>
                    <div className="panel-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetUserPermissions}
                        disabled={savingUser || Object.keys(userOverrides).length === 0}
                      >
                        Reset to Default
                      </Button>
                      <Button
                        onClick={handleSaveUserPermissions}
                        disabled={savingUser}
                      >
                        {savingUser ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <div className="permissions-list">
                    {categories.map(category => (
                      <div key={category.category} className="permission-category">
                        <h4 className="category-title">{category.category}</h4>
                        <div className="permission-items">
                          {category.permissions.map(perm => {
                            const status = getUserPermissionStatus(perm.key);
                            return (
                              <div
                                key={perm.key}
                                className={`permission-item status-${status}`}
                                onClick={() => handleUserPermissionToggle(perm.key)}
                              >
                                <div className="permission-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={status === 'granted' || status === 'override-granted'}
                                    readOnly
                                  />
                                  {status.includes('override') && (
                                    <span className="override-badge">Override</span>
                                  )}
                                </div>
                                <div className="permission-info">
                                  <span className="permission-label">{perm.label}</span>
                                  <span className="permission-desc">{perm.description}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="empty-panel">
                  <Icons.Users />
                  <h3>Select a User</h3>
                  <p>Choose a user from the list to manage their permissions</p>
                </div>
              )}
            </Card>
          </div>
        ) : activeTab === 'roles' ? (
          <div className="two-column-layout">
            <Card className="selection-panel">
              <div className="panel-header">
                <h3>Select Role</h3>
              </div>
              <div className="role-list">
                {roles.map(role => (
                  <div
                    key={role.id}
                    className={`role-item ${selectedRole?.id === role.id ? 'selected' : ''}`}
                    onClick={() => handleSelectRole(role)}
                  >
                    <div className={`role-icon role-${role.name}`}>
                      <Icons.Briefcase />
                    </div>
                    <div className="role-info">
                      <span className="role-name">{role.display_name}</span>
                      <span className="role-desc">
                        {role.is_system_role ? 'System Role' : 'Custom Role'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="permissions-panel">
              {selectedRole ? (
                <>
                  <div className="panel-header">
                    <div>
                      <h3>{selectedRole.display_name}</h3>
                      <p className="panel-subtitle">
                        {selectedRole.description || 'Default permissions for this role'}
                      </p>
                    </div>
                    <div className="panel-actions">
                      <Button
                        onClick={handleSaveRolePermissions}
                        disabled={savingRole}
                      >
                        {savingRole ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <div className="permissions-list">
                    {categories.map(category => (
                      <div key={category.category} className="permission-category">
                        <h4 className="category-title">{category.category}</h4>
                        <div className="permission-items">
                          {category.permissions.map(perm => (
                            <div
                              key={perm.key}
                              className={`permission-item ${rolePermissions[perm.key] ? 'status-granted' : 'status-denied'}`}
                              onClick={() => handleRolePermissionToggle(perm.key)}
                            >
                              <div className="permission-checkbox">
                                <input
                                  type="checkbox"
                                  checked={rolePermissions[perm.key] === true}
                                  readOnly
                                />
                              </div>
                              <div className="permission-info">
                                <span className="permission-label">{perm.label}</span>
                                <span className="permission-desc">{perm.description}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="empty-panel">
                  <Icons.Briefcase />
                  <h3>Select a Role</h3>
                  <p>Choose a role from the list to manage its default permissions</p>
                </div>
              )}
            </Card>
          </div>
        ) : (
          /* Department Timesheet Access Tab */
          <div className="two-column-layout">
            <Card className="selection-panel dept-access-selection" padding="none">
              <div className="panel-header">
                <h3>Select User</h3>
                <span className="user-count">{deptTabTotalUsers} users</span>
              </div>
              
              {/* Search Input */}
              <div className="user-search-box">
                <div className="search-input-wrapper">
                  <Icons.Search className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search by name, email, code..."
                    value={deptTabSearchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDeptTabSearchTerm(val);
                      if (val === '') fetchDeptTabUsers(1, '');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleDeptTabSearch()}
                  />
                  {deptTabSearchTerm && (
                    <button 
                      className="clear-search-btn"
                      onClick={() => {
                        setDeptTabSearchTerm('');
                        fetchDeptTabUsers(1, '');
                      }}
                      title="Clear search"
                    >
                      <Icons.X />
                    </button>
                  )}
                </div>
              </div>

              <div className="user-list">
                {deptTabLoading ? (
                  <div className="loading-users">Loading...</div>
                ) : deptTabUsers.length === 0 ? (
                  <div className="no-users-found">
                    {deptTabSearchTerm ? 'No users match your search' : 'No users found'}
                  </div>
                ) : (
                  deptTabUsers.map(user => (
                    <div
                      key={user.id}
                      className={`user-item ${selectedUserForDept?.id === user.id ? 'selected' : ''}`}
                      onClick={() => handleSelectUserForDept(user)}
                    >
                      <div
                        className="user-avatar"
                        style={{ backgroundColor: stringToColor(user.email) }}
                      >
                        {getInitials(user.name.split(' ')[0], user.name.split(' ')[1] || '')}
                      </div>
                      <div className="user-info">
                        <span className="user-name">
                          {user.name}
                          {user.employee_code && <span className="user-code">({user.employee_code})</span>}
                        </span>
                        <span className="user-roles">
                          {user.roles?.join(', ') || 'No roles'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {deptTabTotalPages > 1 && (
                <div className="user-list-pagination">
                  <button 
                    className="pagination-btn" 
                    onClick={() => handleDeptTabPageChange(1)} 
                    disabled={deptTabCurrentPage === 1 || deptTabLoading}
                    title="First page"
                  >
                    <Icons.ChevronsLeft />
                  </button>
                  <button 
                    className="pagination-btn" 
                    onClick={() => handleDeptTabPageChange(deptTabCurrentPage - 1)} 
                    disabled={deptTabCurrentPage === 1 || deptTabLoading}
                    title="Previous page"
                  >
                    <Icons.ChevronLeft />
                  </button>
                  
                  <span className="pagination-info">
                    {deptTabCurrentPage} / {deptTabTotalPages}
                  </span>

                  <button 
                    className="pagination-btn" 
                    onClick={() => handleDeptTabPageChange(deptTabCurrentPage + 1)} 
                    disabled={deptTabCurrentPage === deptTabTotalPages || deptTabLoading}
                    title="Next page"
                  >
                    <Icons.ChevronRight />
                  </button>
                  <button 
                    className="pagination-btn" 
                    onClick={() => handleDeptTabPageChange(deptTabTotalPages)} 
                    disabled={deptTabCurrentPage === deptTabTotalPages || deptTabLoading}
                    title="Last page"
                  >
                    <Icons.ChevronsRight />
                  </button>
                </div>
              )}
            </Card>

            <Card className="permissions-panel">
              {selectedUserForDept ? (
                <>
                  <div className="panel-header">
                    <div>
                      <h3>{selectedUserForDept.name}</h3>
                      <p className="panel-subtitle">
                        Manage timesheet access for departments
                      </p>
                    </div>
                    <div className="panel-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetDeptAccess}
                        disabled={savingDeptAccess || Object.keys(deptAccessEdits).length === 0}
                      >
                        Clear All
                      </Button>
                      <Button
                        onClick={handleSaveDeptAccess}
                        disabled={savingDeptAccess || !hasDeptAccessChanges()}
                      >
                        {savingDeptAccess ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <div className="dept-access-list">
                    <div className="dept-access-header">
                      <h4>Assigned Departments</h4>
                      <div className="add-dept-container">
                        {showAddDepartment ? (
                          <div className="add-dept-dropdown">
                            <select 
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleAddDepartment(Number(e.target.value));
                                }
                              }}
                              defaultValue=""
                            >
                              <option value="" disabled>Select department...</option>
                              {getAvailableDepartments().map(dept => (
                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                              ))}
                            </select>
                            <button 
                              className="cancel-add-btn"
                              onClick={() => setShowAddDepartment(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAddDepartment(true)}
                            disabled={getAvailableDepartments().length === 0}
                          >
                            <Icons.Plus /> Add Department
                          </Button>
                        )}
                      </div>
                    </div>

                    {Object.keys(deptAccessEdits).length === 0 ? (
                      <div className="no-departments">
                        <p>No department access assigned. Click "Add Department" to grant timesheet access.</p>
                      </div>
                    ) : (
                      <div className="dept-items">
                        {Object.entries(deptAccessEdits).map(([deptIdStr, access]) => {
                          const deptId = Number(deptIdStr);
                          const department = allDepartments.find(d => d.id === deptId);
                          const hasAnyPermission = access.can_view || access.can_approve || access.can_reject || access.can_update;
                          
                          return (
                            <div key={deptId} className={`dept-access-item ${!hasAnyPermission ? 'pending-removal' : ''}`}>
                              <div className="dept-info">
                                <span className="dept-name">{department?.name || `Department ${deptId}`}</span>
                                {!hasAnyPermission && (
                                  <span className="removal-badge">Will be removed</span>
                                )}
                              </div>
                              <div className="dept-permissions">
                                <label className={`perm-checkbox ${access.can_view ? 'checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={access.can_view}
                                    onChange={() => handleDeptPermissionToggle(deptId, 'can_view')}
                                  />
                                  <span>View</span>
                                </label>
                                <label className={`perm-checkbox ${access.can_approve ? 'checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={access.can_approve}
                                    onChange={() => handleDeptPermissionToggle(deptId, 'can_approve')}
                                  />
                                  <span>Approve</span>
                                </label>
                                <label className={`perm-checkbox ${access.can_reject ? 'checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={access.can_reject}
                                    onChange={() => handleDeptPermissionToggle(deptId, 'can_reject')}
                                  />
                                  <span>Reject</span>
                                </label>
                                <label className={`perm-checkbox ${access.can_update ? 'checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={access.can_update}
                                    onChange={() => handleDeptPermissionToggle(deptId, 'can_update')}
                                  />
                                  <span>Update</span>
                                </label>
                              </div>
                              <button 
                                className="remove-dept-btn"
                                onClick={() => handleRemoveDepartment(deptId)}
                                title="Remove department access"
                              >
                                <Icons.Trash />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="dept-access-info">
                      <Icons.AlertCircle />
                      <div>
                        <strong>Permission Details:</strong>
                        <ul>
                          <li><strong>View:</strong> See timesheets of employees in this department</li>
                          <li><strong>Approve:</strong> Approve submitted timesheets</li>
                          <li><strong>Reject:</strong> Reject submitted timesheets</li>
                          <li><strong>Update:</strong> Edit/modify timesheets</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-panel">
                  <Icons.Building />
                  <h3>Select a User</h3>
                  <p>Choose a user from the list to manage their department timesheet access</p>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export default Permissions;
